import {
  ChakraProvider,
  useToast
} from '@chakra-ui/react';
import { QueryClientProvider } from '@tanstack/react-query';
import RedirectAuthorizedUser from 'features/auth/RedirectAuthorizedUser';
import MarketingLayout from 'layouts/marketing';
import { UserRole } from 'models/organization/dto/invite-to-organization.dto';
import { PostHogProvider } from 'posthog-js/react';
import { lazy, Suspense, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ADMIN_LAYOUT_ROLES } from 'routes/access-policy';
import { aesthetics } from './theme/aesthetics';
import './assets/css/App.css';
import { getAppConfig } from './config';
import { AuthProvider, useAuth } from './features/auth/AuthProvider';
import { getUserRole } from './features/auth/roles';
import './i18n';
import AdminLayout from './layouts/admin';
import AuthLayout from './layouts/auth';
import { buildTheme } from './theme/theme';
import { AppSettingsProvider, useAppSettings } from './contexts/AppSettingsContext';
import { createDashboardQueryClient } from './queryClient';

// Onboarding Lab (docs/epics/dashboard/onboarding-lab.md): a fixture-only
// sandbox at #/lab, NEVER in the production build. The condition is a literal
// on the inlined env var so webpack drops the import() — and the Lab's chunk —
// from build:prod entirely; the onlab routine greps the prod bundle to prove it.
const OnboardingLab = process.env.REACT_APP_RUNTIME !== 'prod'
  ? lazy(() => import('./features/onboarding-lab/LabApp'))
  : null;

const posthogOptions = {
  api_host: getAppConfig().posthogConfig?.host || 'https://eu.i.posthog.com',
  disable_session_recording: false,
}

export default function Main() {
  return (
    <QueryClientProviderWrapper>
      <PostHogProvider apiKey={getAppConfig().posthogConfig?.apiKey} options={posthogOptions}>
        <AuthProvider>
          <AppSettingsProvider>
            <App />
          </AppSettingsProvider>
        </AuthProvider>
      </PostHogProvider>
    </QueryClientProviderWrapper>
  )
}
function App() {
  const { themePreset, aesthetic } = useAppSettings();
  const theme = useMemo(() => buildTheme(themePreset, aesthetic), [themePreset, aesthetic]);

  // Dynamically load extra fonts required by the selected aesthetic
  useEffect(() => {
    const aes = aesthetics[aesthetic];
    if (!aes?.extraFonts.length) return;
    const families = aes.extraFonts.join('&family=');
    const href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
    const id = `aesthetic-fonts-${aesthetic}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }, [aesthetic]);

  const { currentUser, loading } = useAuth();

  if (loading) return null;

  return (
    <ChakraProvider theme={theme}>
      <Routes>
        <Route path="marketing/*" element={<MarketingLayout />} />
        {OnboardingLab && (
          <Route path="lab/*" element={<Suspense fallback={null}><OnboardingLab /></Suspense>} />
        )}
        <Route path="auth/*" element={<AuthLayout />} />
        <Route
          path="admin/*"
          element={
            // Coaches share the admin layout, restricted to the sections in
            // `routes/access.ts` (Calendrier / Terrains / Coachs). The old
            // standalone /coach portal is gone — this is the only surface.
            <ProtectedRoute
              element={<AdminLayout />}
              roles={ADMIN_LAYOUT_ROLES}
            />
          }
        />
        <Route
          path="/"
          element={<RedirectAuthorizedUser user={currentUser} />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>
    </ChakraProvider>
  );
}

const QueryClientProviderWrapper = ({ children }: { children?: React.ReactNode }) => {
  const toast = useToast();
  // Both global error handlers (failed reads AND failed writes) live in
  // src/queryClient.ts so they can be tested against a real QueryClient.
  const queryClient = useMemo(() => createDashboardQueryClient(toast), []);
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  )
}

// Exported for tests only — App.tsx is the sole runtime consumer.
export function ProtectedRoute({ element, roles }: { element: JSX.Element; roles: readonly UserRole[] }) {
  const { currentUser } = useAuth();
  if (!currentUser)
    return <Navigate to="/auth" replace />;
  if (!roles.includes(getUserRole(currentUser))) {
    return <Navigate to="/" replace />;
  }

  return element;
}
